/**
 * 推荐码 & 广告渠道管理工具
 *
 * 逻辑与 `bitland-next-frontend/lib/utils/referral.ts` 保持一致：
 * - 同时写入 Cookie（30 天, SameSite=Lax）与 localStorage
 * - 读取时优先 Cookie，其次 localStorage
 * - 推荐码统一大写去空格；渠道统一小写去空格
 * - 渠道访问使用持久化的 visitorId 做去重上报
 */

const REFERRAL_CODE_KEY = 'bitland_referral_code'
const CHANNEL_KEY = 'bitland_channel'
const CHANNEL_VISITED_KEY = 'bitland_channel_visited'
const VISITOR_ID_KEY = 'bitland_visitor_id'
const REFERRAL_CODE_EXPIRY_DAYS = 30

function readCookie(name: string): string | null {
  try {
    const cookies = document.cookie.split(';')
    for (const cookie of cookies) {
      const [n, v] = cookie.trim().split('=')
      if (n === name && v) {
        return v
      }
    }
  } catch {
    // cookie 不可用
  }
  return null
}

function writeCookie(name: string, value: string, days: number): void {
  try {
    const expires = new Date()
    expires.setDate(expires.getDate() + days)
    document.cookie = `${name}=${value}; expires=${expires.toUTCString()}; path=/; SameSite=Lax`
  } catch {
    // cookie 不可用
  }
}

function clearCookie(name: string): void {
  try {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`
  } catch {
    // cookie 不可用
  }
}

/** 保存推荐码到 cookies 和 localStorage */
export function saveReferralCode(code: string): void {
  if (!code) {
    return
  }
  const upperCode = code.toUpperCase().trim()

  try {
    localStorage.setItem(REFERRAL_CODE_KEY, upperCode)
  } catch {
    // localStorage 不可用
  }

  writeCookie(REFERRAL_CODE_KEY, upperCode, REFERRAL_CODE_EXPIRY_DAYS)
}

/** 从 cookies 或 localStorage 获取推荐码 */
export function getReferralCode(): string | null {
  const fromCookie = readCookie(REFERRAL_CODE_KEY)
  if (fromCookie) {
    return fromCookie
  }

  try {
    const code = localStorage.getItem(REFERRAL_CODE_KEY)
    if (code) {
      return code
    }
  } catch {
    // localStorage 不可用
  }

  return null
}

/** 清除推荐码（注册成功后调用） */
export function clearReferralCode(): void {
  try {
    localStorage.removeItem(REFERRAL_CODE_KEY)
  } catch {
    // localStorage 不可用
  }
  clearCookie(REFERRAL_CODE_KEY)
}

/** 从 URL 检测并保存推荐码 */
export function detectAndSaveReferralCode(): string | null {
  if (typeof window === 'undefined') {
    return null
  }

  const urlParams = new URLSearchParams(window.location.search)
  const refCode = urlParams.get('ref')

  if (refCode) {
    saveReferralCode(refCode)
    return refCode.toUpperCase().trim()
  }

  return getReferralCode()
}

/** 保存广告渠道到 cookies 和 localStorage */
export function saveChannel(channel: string): void {
  if (!channel) {
    return
  }
  const ch = channel.trim().toLowerCase()

  try {
    localStorage.setItem(CHANNEL_KEY, ch)
  } catch {
    // localStorage 不可用
  }

  writeCookie(CHANNEL_KEY, ch, REFERRAL_CODE_EXPIRY_DAYS)
}

/** 从 cookies 或 localStorage 获取广告渠道 */
export function getChannel(): string | null {
  const fromCookie = readCookie(CHANNEL_KEY)
  if (fromCookie) {
    return fromCookie
  }

  try {
    const ch = localStorage.getItem(CHANNEL_KEY)
    if (ch) {
      return ch
    }
  } catch {
    // localStorage 不可用
  }

  return null
}

/** 清除广告渠道 */
export function clearChannel(): void {
  try {
    localStorage.removeItem(CHANNEL_KEY)
  } catch {
    // localStorage 不可用
  }
  clearCookie(CHANNEL_KEY)
}

/** 获取或生成持久化的访客 ID */
function getVisitorId(): string {
  try {
    let id = localStorage.getItem(VISITOR_ID_KEY)
    if (!id) {
      id = `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`
      localStorage.setItem(VISITOR_ID_KEY, id)
    }
    return id
  } catch {
    return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`
  }
}

/** 检查该渠道是否已经上报过访问 */
function hasChannelVisited(channel: string): boolean {
  try {
    const visited: unknown = JSON.parse(localStorage.getItem(CHANNEL_VISITED_KEY) || '[]')
    return Array.isArray(visited) && visited.includes(channel)
  } catch {
    return false
  }
}

/** 标记该渠道已上报访问 */
function markChannelVisited(channel: string): void {
  try {
    const visited: unknown = JSON.parse(localStorage.getItem(CHANNEL_VISITED_KEY) || '[]')
    if (Array.isArray(visited) && !visited.includes(channel)) {
      visited.push(channel)
      localStorage.setItem(CHANNEL_VISITED_KEY, JSON.stringify(visited))
    }
  } catch {
    // localStorage 不可用
  }
}

/**
 * 上报渠道访问（每个访客每个渠道只上报一次）
 * 注意：直接用 fetch 绕过 axios 拦截器，避免 401 误跳 /login
 */
export async function reportChannelVisit(channel: string): Promise<void> {
  if (!channel) {
    return
  }

  const ch = channel.trim().toLowerCase()
  if (hasChannelVisited(ch)) {
    return
  }

  const visitorId = getVisitorId()
  const apiBase = import.meta.env.VITE_API_BASE_URL || 'http://localhost:4008/api'

  try {
    await fetch(`${apiBase}/channel-visit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ channel: ch, visitorId }),
    })
    markChannelVisited(ch)
  } catch {
    // 上报失败不影响用户体验
  }
}
